import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
} from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { submit } from "@angular/forms/signals";
import { RouterLink } from "@angular/router";
import { AccountType } from "@front/m19-api-client";
import { AccountSelectionService } from "@front/m19-services";
import {
  ConfirmPopupComponent,
  IButtonDirective,
  IIconComponent,
  IStepperComponent,
  ModalService,
  SpinnerComponent,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { NavBreadcrumbDirective } from "@m19-board/containers/page-layout/nav-breadcrumb.directive";
import {
  FormNavigationService,
  IFormNavigationStep,
  IFormStep,
} from "@m19-board/shared/form-navigation/form-navigation.service";
import { FormShellComponent } from "@m19-board/shared/form-shell/form-shell.component";
import { FormPreviewSplitStateService } from "@m19-board/shared/form-preview-split/form-preview-split-state.service";
import { bindSbWizardCascade } from "@m19-board/strategies/sb-create-strategy/shared/sb-wizard-cascade";
import { SbWizardCreationService } from "@m19-board/strategies/sb-create-strategy/shared/sb-wizard-creation.service";
import { SbWizardForm } from "@m19-board/strategies/sb-create-strategy/shared/sb-wizard-form";
import { SbWizardFormModel } from "@m19-board/strategies/sb-create-strategy/shared/sb-wizard-types";
import { SbCreativeBuilderComponent } from "@m19-board/strategies/sb-create-strategy/steps/sb-creative-builder/sb-creative-builder.component";
import { SbFormAssetsComponent } from "@m19-board/strategies/sb-create-strategy/steps/sb-form-assets/sb-form-assets.component";
import { SbFormFormatComponent } from "@m19-board/strategies/sb-create-strategy/steps/sb-form-format/sb-form-format.component";
import { SbFormSetupComponent } from "@m19-board/strategies/sb-create-strategy/steps/sb-form-setup/sb-form-setup.component";
import { SbPreModerationService } from "@m19-board/strategies/sb-pre-moderation/sb-pre-moderation.service";
import { take } from "rxjs";

@Component({
  selector: "app-sb-wizard-page",
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [FormNavigationService, FormPreviewSplitStateService, SbPreModerationService],
  imports: [
    FormShellComponent,
    IStepperComponent,
    SbFormSetupComponent,
    SbFormFormatComponent,
    SbFormAssetsComponent,
    SbCreativeBuilderComponent,
    IButtonDirective,
    IIconComponent,
    SpinnerComponent,
    TranslocoDirective,
    NavBreadcrumbDirective,
    RouterLink,
  ],
  templateUrl: "./sb-wizard-page.component.html",
})
export class SbWizardPageComponent implements OnInit {
  private readonly formNavigationService = inject(FormNavigationService<SbWizardFormModel>);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly modalService = inject(ModalService);
  private readonly translocoService = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);
  readonly preModeration = inject(SbPreModerationService);
  private readonly sbWizardCreationService = inject(SbWizardCreationService);

  readonly form = input.required<SbWizardForm>();
  readonly steps = input.required<IFormNavigationStep<SbWizardFormModel>[]>();
  readonly submitLabelKey = input("common.create_strategy");
  readonly ready = input(true);
  readonly breadcrumbLink = input<(string | number)[] | null>(null);
  readonly strategyName = input("");
  readonly disableStoreSpotlight = input(false);
  readonly disableManualCollection = input(false);
  readonly tacosSpAsins = input<string[] | undefined>(undefined);
  readonly hideSetupNameAndAlgo = input(false, { transform: booleanAttribute });

  readonly cancelled = output<void>();

  private readonly accountMarketplace = toSignal(this.accountSelectionService.singleAccountMarketplaceSelection$, {
    requireSync: true,
  });
  private readonly isVendor = computed(() => this.accountMarketplace().accountType === AccountType.VENDOR);

  private readonly navigationBlocked = computed(
    () => this.sbWizardCreationService.isSubmitting() || this.preModeration.loading(),
  );

  readonly currentStepId = this.formNavigationService.currentStepId;
  readonly formSteps = this.formNavigationService.formSteps;
  readonly isFirstStep = this.formNavigationService.isFirstStep;
  readonly isLastStep = this.formNavigationService.isLastStep;
  readonly canGoNext = this.formNavigationService.canGoNext;
  readonly isSubmitting = this.sbWizardCreationService.isSubmitting;
  readonly showRelaunchPreModeration = computed(() => {
    if (this.currentStepId() !== "assets") return false;
    if (!this.preModeration.hasLaunched()) return false;
    const form = this.form();
    return !this.preModeration.shouldSkip(
      form.format.sbCreativeType().value(),
      form.assets.headline().value(),
      form.assets.brandLogo().value(),
    );
  });

  constructor() {
    bindSbWizardCascade({
      ready: this.ready,
      creationForm: this.form,
      isVendor: this.isVendor,
      formNavigation: this.formNavigationService,
      preModeration: this.preModeration,
    });
  }

  ngOnInit(): void {
    this.formNavigationService.configure({
      form: this.form(),
      blocked: this.navigationBlocked,
      steps: this.steps(),
    });
  }

  goToStep(stepId: IFormStep<SbWizardFormModel>) {
    this.formNavigationService.goToStep(stepId);
  }

  back() {
    this.formNavigationService.back();
  }

  next() {
    if (this.currentStepId() === "assets") {
      this.nextFromAssets();
      return;
    }
    this.formNavigationService.next();
  }

  cancel() {
    this.cancelled.emit();
  }

  relaunchPreModeration() {
    const assets = this.form().assets;
    this.preModeration.launch(assets.headline().value(), assets.brandLogo().value()?.url);
  }

  onSubmit() {
    if (this.form()().invalid() || this.sbWizardCreationService.isSubmitting()) return;
    void submit(this.form());
  }

  private nextFromAssets() {
    if (!this.canGoNext()) return;

    const form = this.form();
    const creativeType = form.format.sbCreativeType().value();
    const headline = form.assets.headline().value();
    const brandLogo = form.assets.brandLogo().value();

    if (this.preModeration.warning()) {
      this.confirmPreModWarningsThenNext();
      return;
    }

    if (!this.preModeration.hasLaunched() && !this.preModeration.shouldSkip(creativeType, headline, brandLogo)) {
      this.preModeration.launch(headline, brandLogo?.url, () => this.formNavigationService.next());
      return;
    }

    this.formNavigationService.next();
  }

  private confirmPreModWarningsThenNext() {
    const ref = this.modalService.openModal(ConfirmPopupComponent, {
      modalTitle: this.translocoService.translate("sb-form-creative.pre-moderation_warnings"),
      data: {
        message: this.translocoService.translate("sb-form-creative.are_you_sure_you_want_to_proceed"),
      },
    });

    ref
      .afterClosed()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.formNavigationService.next());
  }
}
